import React, { useState, useEffect, FC, useRef } from "react";
import Content from "../../../layout/content/Content";
import { Card, Row, Col } from "reactstrap";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  BlockDes,
  Button,
  Icon,
  PaginationComponent,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  DataTableItem,
  TooltipComponent,
} from "../../../components/Component";
import Swal from "sweetalert2";
import contractorsApis from "../../../api/master/contractors";
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Badge,
} from "reactstrap";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import Export from "../../../components/export/Export";
import { useSelector } from "react-redux";
import { Table, Spin, Flex, Empty } from "antd";

interface Contractor {
  contractorID: number;
  referenceID: string;
  companyName: string;
  gstin: string;
  contactPerson: string;
  phoneNumber: string;
  email: string;
  status: string;
  paginationId: number;
}

function useDebounce(value: string, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const Contractors: FC = () => {
  const { data: accessibility } = useSelector(
    (state: any) => state.accessibility
  );
  const [allowedActions, setAllowedActions] = useState<any>({
    add: 0,
    view: 0,
    edit: 0,
    approve: 0,
    reject: 0,
    cancel: 0,
  });

  useEffect(() => {
    if (accessibility) {
      try {
        const { add, view, edit, approve, reject, cancel } =
          accessibility["Master"];
        setAllowedActions({ add, view, edit, approve, reject, cancel });
      } catch (error) {
        console.log(error);
      }
    }
  }, [accessibility]);

  const [sm, updateSm] = useState(false);
  const [onSearchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(onSearchText, 300);
  const [sort, setSortState] = useState("DESC");
  const [itemPerPage, setItemPerPage] = useState(10);
  const navigate = useNavigate();

  // Pagination state
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    sortOrder: "DESC" as "ASC" | "DESC",
    direction: "P" as "P" | "F" | "B",
    pageDiff: 0,
    paginationId: null as number | null,
  });

  const [dataSource, setDataSource] = useState<Contractor[]>([]);
  const [loading, setLoading] = useState(false);
  const [originalData, setOriginalData] = useState<Contractor[]>([]);

  const fetchData = async (params: any = {}) => {
    setLoading(true);
    try {
      const apiParams = {
        sortOrder: params.sortOrder || pagination.sortOrder,
        direction: params.direction || pagination.direction,
        pageSize: params.pageSize || pagination.pageSize,
        pageDiff: params.pageDiff || pagination.pageDiff,
        paginationId: params.paginationId || pagination.paginationId,
        searchQuery:
          params.searchQuery !== undefined ? params.searchQuery : onSearchText,
        partyType: "Contractor",
      };

      const { data, message, status } =
        await contractorsApis.fetchContractorWithPagination(apiParams);

      if (status) {
        const { list, count } = data;

        setDataSource(list || []);
        setOriginalData(list || []);
        setPagination((prev) => ({
          ...prev,
          total: count || 0,
          ...(params.current && { current: params.current }),
          ...(params.pageSize && { pageSize: params.pageSize }),
          ...(params.sortOrder && { sortOrder: params.sortOrder }),
          ...(params.direction && { direction: params.direction }),
          ...(params.pageDiff && { pageDiff: params.pageDiff }),
          ...(params.paginationId !== undefined && {
            paginationId: params.paginationId,
          }),
        }));
      } else {
        Swal.fire("Error", message, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id: any) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      allowOutsideClick: false,
      buttonsStyling: false,
      customClass: {
        confirmButton: "btn btn-primary",
        cancelButton: "btn btn-secondary",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        deleteContractorById(id);
      }
    });
  };

  const deleteContractorById = async (id: any) => {
    const { status, message } = await contractorsApis.deleteContractorById(id);
    if (status) {
      Swal.fire("Deleted!", "Contractor has been deleted", "success");
      fetchData({
        current: 1,
        pageDiff: 0,
        paginationId: null,
        direction: "P",
      });
    } else {
      Swal.fire("Error", message, "error");
    }
  };

  const addContractors = () => {
    navigate(`/addContractors`);
  };

  const sortFunc = (params: any) => {
    let defaultData = [...dataSource];
    if (params === "asc") {
      let sortedData = defaultData.sort((a: any, b: any) =>
        a.companyName.localeCompare(b.companyName)
      );
      setDataSource([...sortedData]);
    } else if (params === "dsc") {
      let sortedData = defaultData.sort((a: any, b: any) =>
        b.companyName.localeCompare(a.companyName)
      );
      setDataSource([...sortedData]);
    }
  };

  // const onFilterChange = (e: any) => {
  //   const searchText = e.target.value;
  //   setSearchText(searchText);

  //   if (searchText !== "") {
  //     const filteredObject = originalData.filter((item: any) => {
  //       return item.companyName
  //         .toLowerCase()
  //         .includes(searchText.toLowerCase());
  //     });
  //     setDataSource([...filteredObject]);
  //   } else {
  //     setDataSource([...originalData]);
  //   }
  // };

  const handleSearch = () => {
    fetchData({
      current: 1,
      pageDiff: 0,
      paginationId: null,
      direction: "P",
      searchQuery: onSearchText.trim(),
    });
  };

  useEffect(() => {
    fetchData({
      current: 1,
      pageDiff: 0,
      paginationId: null,
      direction: "P",
      searchQuery: debouncedSearch.trim(),
    });
  }, [debouncedSearch]);

  const handleItemsPerPageChange = (newItemPerPage: number) => {
    setItemPerPage(newItemPerPage);
    fetchData({
      pageSize: newItemPerPage,
      current: 1,
      pageDiff: 0,
      paginationId: null,
      direction: "P",
    });
  };

  const handleSort = (sortOrder: string) => {
    setSortState(sortOrder);
    fetchData({
      sortOrder: sortOrder === "asc" ? "ASC" : "DESC",
      current: 1,
      pageDiff: 0,
      paginationId: null,
      direction: "P",
    });
  };

  const tableOnChange = async (
    newPagination: any,
    filters: any,
    sorter: any
  ) => {
    const apiParams: any = {
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      pageDiff: 0,
      paginationId: null,
      direction: "P",
    };

    if (sorter.order) {
      apiParams.sortOrder = sorter.order === "ascend" ? "ASC" : "DESC";
    } else if (newPagination.pageSize !== pagination.pageSize) {
      apiParams.pageDiff = Math.floor(
        ((pagination.current - 1) * pagination.pageSize) /
          newPagination.pageSize
      );
    } else if (newPagination.current !== pagination.current) {
      apiParams.direction =
        newPagination.current > pagination.current ? "F" : "B";
      apiParams.pageDiff =
        Math.abs(newPagination.current - pagination.current) - 1;

      if (apiParams.direction === "F" && dataSource.length > 0) {
        apiParams.paginationId =
          dataSource[dataSource.length - 1]?.paginationId;
      } else if (apiParams.direction === "B" && dataSource.length > 0) {
        apiParams.paginationId = dataSource[0]?.paginationId;
      }
    }

    await fetchData(apiParams);
  };

  useEffect(() => {
    fetchData({ current: 1, pageDiff: 0, paginationId: null, direction: "P" });
  }, []);

  const columns = [
    {
      title: "Internal ID",
      dataIndex: "referenceID",
      key: "referenceID",
      width: 120,
    },
    {
      title: "General Contractor Name",
      dataIndex: "companyName",
      key: "companyName",
      width: 200,
      sorter: true,
    },
    {
      title: "GSTIN",
      dataIndex: "gstin",
      key: "gstin",
      width: 150,
      render: (gstin: string) => gstin || "N/A",
    },
    {
      title: "Contact Person",
      dataIndex: "contactPerson",
      key: "contactPerson",
      width: 150,
    },
    {
      title: "Phone Number",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      width: 130,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 200,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status: string) => (
        <span
          className={`tb-status text-${
            status === "Active" ? "success" : "danger"
          }`}
        >
          {status}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      fixed: "right" as const,
      render: (record: any) => (
        <UncontrolledDropdown>
          <DropdownToggle
            tag="a"
            className="dropdown-toggle btn btn-icon btn-trigger"
          >
            <Icon name="more-h"></Icon>
          </DropdownToggle>
          <DropdownMenu end>
            <ul className="link-list-opt no-bdr">
              <li>
                <Link
                  to={`${process.env.PUBLIC_URL}/viewContractorDetails/${record.contractorID}`}
                >
                  <Icon name="focus"></Icon>
                  <span>View Details</span>
                </Link>
              </li>
              {allowedActions?.edit && (
                <li onClick={() => handleDelete(record.contractorID)}>
                  <a href="#">
                    <Icon name="trash"></Icon>
                    <span>Delete user</span>
                  </a>
                </li>
              )}
            </ul>
          </DropdownMenu>
        </UncontrolledDropdown>
      ),
    },
  ];

  return (
    <React.Fragment>
      <Head title="Contractors"></Head>
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h3" page>
                Contractors
              </BlockTitle>
              <BlockDes className="text-soft">
                <p>You have total {pagination.total} Contractors.</p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="toggle-wrap nk-block-tools-toggle">
                <Button
                  className={`btn-icon btn-trigger toggle-expand me-n1 ${
                    sm ? "active" : ""
                  }`}
                  onClick={() => updateSm(!sm)}
                >
                  <Icon name="menu-alt-r"></Icon>
                </Button>
                <div
                  className="toggle-expand-content"
                  style={{ display: sm ? "block" : "none" }}
                >
                  <ul className="nk-block-tools g-3">
                    <li>
                      <Export data={dataSource} />
                    </li>
                    {allowedActions?.add && (
                      <li className="nk-block-tools-opt">
                        <Button color="primary" onClick={addContractors}>
                          <Icon name="plus"></Icon>
                          <span>Add</span>
                        </Button>
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <Card className="card-bordered">
            <div className="card-inner position-relative card-tools-toggle">
              <div className="card-search search-wrap active">
                <div className="card-body">
                  <div
                    className="search-content"
                    style={{ whiteSpace: "nowrap", display: "flex" }}
                  >
                    <Button className="search-back btn-icon toggle-search active">
                      <Icon name="search"></Icon>
                    </Button>
                    <input
                      type="text"
                      className="border-transparent form-focus-none form-control"
                      // placeholder="Search by Company Name"
                      placeholder="Search by ID, Company, GSTIN, PAN, etc."
                      value={onSearchText}
                      // onChange={onFilterChange}
                      onChange={(e) => setSearchText(e.target.value)}
                    />
                    <UncontrolledDropdown>
                      <DropdownToggle
                        tag="a"
                        className="btn btn-trigger btn-icon dropdown-toggle"
                      >
                        <Icon name="filter"></Icon>
                      </DropdownToggle>
                      <DropdownMenu end className="dropdown-menu-xs">
                        <ul className="link-check">
                          <li>
                            <span>Show</span>
                          </li>
                          <li className={itemPerPage === 10 ? "active" : ""}>
                            <DropdownItem
                              tag="a"
                              href="#dropdownitem"
                              onClick={(ev) => {
                                ev.preventDefault();
                                handleItemsPerPageChange(10);
                              }}
                            >
                              10
                            </DropdownItem>
                          </li>
                          <li className={itemPerPage === 15 ? "active" : ""}>
                            <DropdownItem
                              tag="a"
                              href="#dropdownitem"
                              onClick={(ev) => {
                                ev.preventDefault();
                                handleItemsPerPageChange(15);
                              }}
                            >
                              15
                            </DropdownItem>
                          </li>
                        </ul>
                        <ul className="link-check">
                          <li>
                            <span>Order</span>
                          </li>
                          <li className={sort === "dsc" ? "active" : ""}>
                            <DropdownItem
                              tag="a"
                              href="#dropdownitem"
                              onClick={(ev) => {
                                ev.preventDefault();
                                handleSort("dsc");
                              }}
                            >
                              DESC
                            </DropdownItem>
                          </li>
                          <li className={sort === "asc" ? "active" : ""}>
                            <DropdownItem
                              tag="a"
                              href="#dropdownitem"
                              onClick={(ev) => {
                                ev.preventDefault();
                                handleSort("asc");
                              }}
                            >
                              ASC
                            </DropdownItem>
                          </li>
                        </ul>
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  </div>
                </div>
              </div>
            </div>

            <div className="card-inner">
              {loading ? (
                <Flex
                  align="center"
                  justify="center"
                  style={{ height: "200px" }}
                >
                  <Spin size="large" />
                </Flex>
              ) : dataSource.length > 0 ? (
                <Table
                  columns={columns}
                  dataSource={dataSource}
                  rowKey="contractorID"
                  pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    // showSizeChanger: true,
                    // pageSizeOptions: ["10", "20", "50", "100"],
                    // showTotal: (total, range) =>
                    //   `${range[0]}-${range[1]} of ${total} items`,
                  }}
                  onChange={tableOnChange}
                  scroll={{ x: 1000 }}
                  bordered
                />
              ) : (
                <Empty description="No contractors found" />
              )}
            </div>
          </Card>
        </Block>
      </Content>
    </React.Fragment>
  );
};

export default Contractors;
